import { STATUS } from "@/constants/statuses";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import type { Observable, Subscription } from "rxjs";
import {
    catchError,
    distinctUntilChanged,
    lastValueFrom,
    retry,
    switchMap,
    tap,
    timer,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { rewardsApplicationLoggerToken } from "../../../tokens";
import RewardsCustomerRewardsApiService from "../api_service/RewardsCustomerRewardsApiService";
import type {
    CustomerActiveRewardsResponseType,
    CustomerDelayedRewardsResponseType,
} from "../models/RewardsCustomerRewardsEntities";
import RewardsCustomerActiveRewardsStore from "../stores/RewardsCustomerActiveRewardsStore";
import RewardsCustomerDelayedRewardsStore from "../stores/RewardsCustomerDelayedRewardsStore";
import type { CustomerRewardsQueryParams } from "../types";

@singleton()
class RewardsCustomerRewardsService {
    private subscribersCount = 0;
    private intervalRequestSubscription: Subscription | null = null;

    constructor(
        private readonly customerRewardsApiService: RewardsCustomerRewardsApiService,
        private readonly customerActiveRewardsStore: RewardsCustomerActiveRewardsStore,
        private readonly customerDelayedRewardsStore: RewardsCustomerDelayedRewardsStore,
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getCustomerActiveRewards(
        queryParams: CustomerRewardsQueryParams
    ): Observable<CustomerActiveRewardsResponseType> {
        this.customerActiveRewardsStore.setStatus(
            getLoadingStatus(this.customerActiveRewardsStore.getStatus())
        );

        return this.customerRewardsApiService
            .getCustomerActiveRewards(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsCustomerRewardsApiService.getCustomerActiveRewards is errored",
                        error
                    );
                    this.customerActiveRewardsStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsCustomerRewardsApiService.getCustomerActiveRewards is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.customerActiveRewardsStore.update((state) => ({
                            ...state,
                            customerActiveRewardsList: value.rewards,
                        }));
                        this.customerActiveRewardsStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    getCustomerActiveRewardsWithInterval(
        queryParams: CustomerRewardsQueryParams
    ): Observable<CustomerActiveRewardsResponseType> {
        return timer(2500, 5000).pipe(
            switchMap(() =>
                this.customerRewardsApiService.getCustomerActiveRewards(
                    queryParams
                )
            ),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsCustomerRewardsApiService.getCustomerActiveRewardsWithInterval is errored",
                    error
                );
                this.customerActiveRewardsStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            distinctUntilChanged(
                (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            ),
            tap((value) => {
                this.logger.debug(
                    "RewardsCustomerRewardsApiService.getCustomerActiveRewardsWithInterval is ready"
                );
                //Store Update
                this.customerActiveRewardsStore.update((state) => ({
                    ...state,
                    customerActiveRewardsList: value.rewards,
                }));
            }),
            retry({ delay: 1000 })
        );
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetCustomerActiveRewards(
        queryParams: CustomerRewardsQueryParams
    ): Promise<void> {
        if (this.customerActiveRewardsStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getCustomerActiveRewards(queryParams));
    }

    getCustomerDelayedRewards(
        queryParams: CustomerRewardsQueryParams
    ): Observable<CustomerDelayedRewardsResponseType> {
        this.customerDelayedRewardsStore.setStatus(
            getLoadingStatus(this.customerDelayedRewardsStore.getStatus())
        );

        return this.customerRewardsApiService
            .getCustomerDelayedRewards(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsCustomerRewardsApiService.getCustomerDelayedRewards is errored",
                        error
                    );
                    this.customerDelayedRewardsStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsCustomerRewardsApiService.getCustomerDelayedRewards is ready"
                    );
                    // Store Update
                    applyTransaction(() => {
                        this.customerDelayedRewardsStore.update((state) => ({
                            ...state,
                            customerDelayedRewardsList: value.delayedRewards,
                        }));
                        this.customerDelayedRewardsStore.setStatus(
                            STATUS.READY
                        );
                    });
                })
            );
    }

    public startIntervalRequests = (
        queryParams: CustomerRewardsQueryParams
    ) => {
        if (!this.intervalRequestSubscription) {
            this.intervalRequestSubscription =
                this.getCustomerActiveRewardsWithInterval(
                    queryParams
                ).subscribe();
        }

        this.subscribersCount++;
    };

    public stopIntervalRequests = () => {
        if (this.intervalRequestSubscription && this.subscribersCount <= 1) {
            this.intervalRequestSubscription.unsubscribe();
            this.intervalRequestSubscription = null;
        }

        this.subscribersCount--;
    };
}

export default RewardsCustomerRewardsService;
